import React, { useEffect, useState, useCallback, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, subDays, startOfWeek, startOfMonth } from "date-fns";
import {
  PhoneIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  BuildingOfficeIcon,
  HandThumbDownIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../contexts/AuthContext";
import { Link } from "react-router-dom";
import Button from "../../components/UI/Button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/UI/Card";
import StatBox from "../../components/UI/StatBox";
import {
  dashboardService,
  DashboardStats,
  CampaignMetricsBreakdown,
} from "../../services/dashboardService";
// Charts removed from dashboard

// DashboardStats interface is now imported from dashboardService

const DashboardPage: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [campaignMetrics, setCampaignMetrics] = useState<CampaignMetricsBreakdown[] | null>(null);
  // Chart data removed from dashboard
  const [loading, setLoading] = useState(true);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [campaignRowsVisible, setCampaignRowsVisible] = useState(10);

  // Date range state
  const [dateMode, setDateMode] = useState<
    "today" | "7days" | "30days" | "this_week" | "this_month" | "range" | "all"
  >("today");
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);

  const fetchDashboardData = useCallback(
    async (isInitial = false) => {
      // Don't fetch if range mode but dates not selected yet
      if (dateMode === "range" && (!rangeStart || !rangeEnd)) return;

      try {
        if (isInitial) {
          setLoading(true);
        } else {
          setIsRefreshing(true);
        }
        setFetchError(null);

        // Build date params
        let sd: string | undefined;
        let ed: string | undefined;
        const now = new Date();
        if (dateMode === "today") {
          const today = format(now, "yyyy-MM-dd");
          sd = today;
          ed = today + "T23:59:59";
        } else if (dateMode === "7days") {
          sd = format(subDays(now, 7), "yyyy-MM-dd");
          ed = format(now, "yyyy-MM-dd") + "T23:59:59";
        } else if (dateMode === "30days") {
          sd = format(subDays(now, 30), "yyyy-MM-dd");
          ed = format(now, "yyyy-MM-dd") + "T23:59:59";
        } else if (dateMode === "this_week") {
          sd = format(startOfWeek(now, { weekStartsOn: 1 }), "yyyy-MM-dd");
          ed = format(now, "yyyy-MM-dd") + "T23:59:59";
        } else if (dateMode === "this_month") {
          sd = format(startOfMonth(now), "yyyy-MM-dd");
          ed = format(now, "yyyy-MM-dd") + "T23:59:59";
        } else if (dateMode === "range" && rangeStart && rangeEnd) {
          sd = format(rangeStart, "yyyy-MM-dd");
          ed = format(rangeEnd, "yyyy-MM-dd") + "T23:59:59";
        }

        // Fetch both stats and campaign metrics in a single backend request.
        const snapshot = await dashboardService.getDashboardSnapshot(sd, ed);
        const statsData = snapshot.stats;
        const campaignMetricsData = snapshot.campaignMetrics;

        setStats(statsData);
        setCampaignMetrics(campaignMetricsData);
        setCampaignRowsVisible(10);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
        setFetchError(
          error instanceof Error
            ? error.message
            : "Failed to load dashboard data",
        );
        // Set default empty state on error
        setCampaignMetrics([]);
        setStats({
          totalCampaigns: 0,
          activeCampaigns: 0,
          totalCalls: 0,
          manualCalls: 0,
          autoCalls: 0,
          activeCalls: 0,
          totalLeads: 0,
          remainingLeads: 0,
          calledLeads: 0,
          interestedLeads: 0,
          notInterestedLeads: 0,
          followUpLeads: 0,
          noAnswerLeads: 0,
          doNotCallLeads: 0,
          failedLeads: 0,
          badNumberLeads: 0,
          wrongNumberLeads: 0,
          outOfBusinessLeads: 0,
          unqualifiedLeads: 0,
          dialedCalls: 0,
          queuedCalls: 0,
          completedCalls: 0,
          successRate: 0,
          sms: {
            totalSent: 0,
            inboundReplies: 0,
            openThreads: 0,
            dncBlocks: 0,
          },
        });
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    },
    [dateMode, rangeStart, rangeEnd],
  );

  // Initial load
  useEffect(() => {
    fetchDashboardData(true);

    // Safety timeout: if loading persists for >8s, force clear it
    const safetyTimeout = setTimeout(() => {
      setLoading((prev) => {
        if (prev) {
          console.warn("Dashboard loading safety timeout triggered");
          return false;
        }
        return prev;
      });
    }, 8000);

    return () => clearTimeout(safetyTimeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced re-fetch on filter change (not initial)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!loading) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchDashboardData(false);
      }, 400);
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [dateMode, rangeStart, rangeEnd]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (fetchError && !stats) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="text-center">
          <p className="text-destructive font-medium mb-2">
            Failed to load dashboard
          </p>
          <p className="text-muted-foreground text-sm mb-4">{fetchError}</p>
          <button
            onClick={() => fetchDashboardData(true)}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const leadStatusCards = [
    {
      name: "Total Leads",
      value: stats?.totalLeads || 0,
      icon: UserGroupIcon,
      bgColor: "bg-primary/10",
      iconColor: "text-primary",
      variant: "primary" as const,
    },
    {
      name: "New",
      value: stats?.remainingLeads || 0,
      icon: UserGroupIcon,
      bgColor: "bg-muted/50",
      iconColor: "text-foreground",
      variant: "default" as const,
    },
    {
      name: "Called",
      value: stats?.calledLeads || 0,
      icon: PhoneIcon,
      bgColor: "bg-primary/10",
      iconColor: "text-primary",
      variant: "primary" as const,
    },
    {
      name: "Interested",
      value: stats?.interestedLeads || 0,
      icon: CheckCircleIcon,
      bgColor: "bg-success/10",
      iconColor: "text-success",
      variant: "success" as const,
    },
    {
      name: "Not Interested",
      value: stats?.notInterestedLeads || 0,
      icon: XCircleIcon,
      bgColor: "bg-destructive/10",
      iconColor: "text-destructive",
      variant: "destructive" as const,
    },
    {
      name: "Follow Up",
      value: stats?.followUpLeads || 0,
      icon: CalendarDaysIcon,
      bgColor: "bg-primary/10",
      iconColor: "text-primary",
      variant: "primary" as const,
    },
    {
      name: "No Answer",
      value: stats?.noAnswerLeads || 0,
      icon: ClockIcon,
      bgColor: "bg-warning/10",
      iconColor: "text-warning",
      variant: "warning" as const,
    },
    {
      name: "Do Not Call",
      value: stats?.doNotCallLeads || 0,
      icon: NoSymbolIcon,
      bgColor: "bg-destructive/10",
      iconColor: "text-destructive",
      variant: "destructive" as const,
    },
    {
      name: "Failed",
      value: stats?.failedLeads || 0,
      icon: ExclamationTriangleIcon,
      bgColor: "bg-warning/10",
      iconColor: "text-warning",
      variant: "warning" as const,
    },
    {
      name: "Unreachable",
      value: stats?.badNumberLeads || 0,
      icon: ExclamationTriangleIcon,
      bgColor: "bg-orange-500/10",
      iconColor: "text-orange-500",
      variant: "warning" as const,
    },
    {
      name: "Wrong Number",
      value: stats?.wrongNumberLeads || 0,
      icon: NoSymbolIcon,
      bgColor: "bg-red-400/10",
      iconColor: "text-red-400",
      variant: "destructive" as const,
    },
    {
      name: "Out of Business",
      value: stats?.outOfBusinessLeads || 0,
      icon: BuildingOfficeIcon,
      bgColor: "bg-slate-500/10",
      iconColor: "text-slate-500",
      variant: "default" as const,
    },
    {
      name: "Unqualified",
      value: stats?.unqualifiedLeads || 0,
      icon: HandThumbDownIcon,
      bgColor: "bg-muted",
      iconColor: "text-muted-foreground",
      variant: "default" as const,
    },
  ];
  const callActivityCards = [
    {
      name: "Total Calls",
      value: stats?.totalCalls || 0,
      icon: PhoneIcon,
      bgColor: "bg-muted/50",
      iconColor: "text-foreground",
      variant: "default" as const,
    },
    {
      name: "Manual Dial Calls",
      value: stats?.manualCalls || 0,
      icon: PhoneIcon,
      bgColor: "bg-warning/10",
      iconColor: "text-warning",
      variant: "warning" as const,
    },
    {
      name: "Auto Dial Calls",
      value: stats?.autoCalls || 0,
      icon: PhoneIcon,
      bgColor: "bg-primary/10",
      iconColor: "text-primary",
      variant: "primary" as const,
    },
    {
      name: "Dialed Calls",
      value: stats?.dialedCalls || 0,
      icon: ClockIcon,
      bgColor: "bg-warning/10",
      iconColor: "text-warning",
      variant: "warning" as const,
    },
    {
      name: "Queued Calls",
      value: stats?.queuedCalls || 0,
      icon: ClockIcon,
      bgColor: "bg-muted/50",
      iconColor: "text-muted-foreground",
      variant: "default" as const,
    },
    {
      name: "Active Calls",
      value: stats?.activeCalls || 0,
      icon: PhoneIcon,
      bgColor: "bg-primary/10",
      iconColor: "text-primary",
      variant: "primary" as const,
    },
  ];
  const smsStatCards = [
    {
      name: "Total Sent",
      value: stats?.sms?.totalSent || 0,
      icon: ChatBubbleLeftRightIcon,
      bgColor: "bg-primary/10",
      iconColor: "text-primary",
      variant: "primary" as const,
    },
    {
      name: "Inbound Replies",
      value: stats?.sms?.inboundReplies || 0,
      icon: ChatBubbleLeftRightIcon,
      bgColor: "bg-success/10",
      iconColor: "text-success",
      variant: "success" as const,
    },
    {
      name: "Open Threads",
      value: stats?.sms?.openThreads || 0,
      icon: ClockIcon,
      bgColor: "bg-warning/10",
      iconColor: "text-warning",
      variant: "warning" as const,
    },
    {
      name: "DNC Blocks",
      value: stats?.sms?.dncBlocks || 0,
      icon: NoSymbolIcon,
      bgColor: "bg-destructive/10",
      iconColor: "text-destructive",
      variant: "destructive" as const,
    },
  ];

  const CAMPAIGN_ROWS_PAGE = 10;

  const campaignRecencyMs = (campaign: CampaignMetricsBreakdown): number => {
    const lastCall = campaign.lastCallAt ? Date.parse(String(campaign.lastCallAt)) : 0;
    const updated = campaign.updatedAt ? Date.parse(String(campaign.updatedAt)) : 0;
    const created = campaign.createdAt ? Date.parse(String(campaign.createdAt)) : 0;
    return Math.max(
      Number.isFinite(lastCall) ? lastCall : 0,
      Number.isFinite(updated) ? updated : 0,
      Number.isFinite(created) ? created : 0
    );
  };

  const campaignRows = (campaignMetrics || [])
    .filter((campaign) => campaign.campaignStatus?.toLowerCase() !== "trash")
    .slice()
    .sort((a, b) => campaignRecencyMs(b) - campaignRecencyMs(a));
  const visibleCampaignRows = campaignRows.slice(0, campaignRowsVisible);
  const hasMoreCampaignRows = campaignRowsVisible < campaignRows.length;

  const dateFilterLabel =
    dateMode === "today"
      ? "today"
      : dateMode === "7days"
        ? "the last 7 days"
        : dateMode === "30days"
          ? "the last 30 days"
          : dateMode === "this_week"
            ? "this week"
            : dateMode === "this_month"
              ? "this month"
              : dateMode === "range"
                ? "the selected range"
                : "all time";

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between py-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Welcome back, {user?.name}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Remaining lead statuses across your campaigns. Call activity uses the date filter.
          </p>
        </div>
        <div className="hidden md:flex items-center space-x-3">
          <Button variant="outlined" size="sm" to="/campaigns">
            View Campaigns
          </Button>
          {isAdmin && (
            <Button variant="primary" size="sm" to="/campaigns/create">
              Create Campaign
            </Button>
          )}
        </div>
      </div>

      {/* Date Range Selector */}
      <Card className="overflow-visible">
        <CardContent className="px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center space-x-2 flex-shrink-0">
              <CalendarDaysIcon className="h-5 w-5 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">
                Filter:
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {(
                [
                  { key: "all", label: "All Time" },
                  { key: "today", label: "Today" },
                  { key: "7days", label: "Last 7 Days" },
                  { key: "30days", label: "Last 30 Days" },
                  { key: "this_week", label: "This Week" },
                  { key: "this_month", label: "This Month" },
                  { key: "range", label: "Custom Range" },
                ] as const
              ).map((btn) => (
                <button
                  key={btn.key}
                  onClick={() => setDateMode(btn.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 ${
                    dateMode === btn.key
                      ? "bg-primary text-primary-foreground shadow-md"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  {btn.label}
                </button>
              ))}
            </div>
            <div
              className={`dashboard-datepicker transition-all duration-300 ease-in-out flex-shrink-0 ${
                dateMode === "range"
                  ? "w-56 opacity-100"
                  : "w-0 opacity-0 pointer-events-none overflow-hidden"
              }`}
            >
              <DatePicker
                selectsRange
                startDate={rangeStart}
                endDate={rangeEnd}
                onChange={(update: [Date | null, Date | null]) => {
                  setRangeStart(update[0]);
                  setRangeEnd(update[1]);
                }}
                isClearable
                placeholderText="Select date range"
                className="px-3 py-1.5 text-sm rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary w-56"
                dateFormat="MMM d, yyyy"
                maxDate={new Date()}
                monthsShown={2}
                portalId="datepicker-portal"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Date filter applies to call activity only. Lead statuses are remaining inventory after duplicate delete.
          </p>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="relative">
        {isRefreshing && (
          <div className="absolute inset-0 bg-background/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent"></div>
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 stagger-fade-in">
          {leadStatusCards.map((stat, index) => (
            <div
              key={stat.name}
              className="animate-fade-in"
              style={{ animationDelay: `${index * 40}ms` }}
            >
              <StatBox
                title={stat.name}
                value={stat.value}
                icon={stat.icon}
                iconBgColor={stat.bgColor}
                iconColor={stat.iconColor}
                variant={stat.variant}
              />
            </div>
          ))}
        </div>
      </div>

      {isAdmin && (
        <div className="grid grid-cols-1">
          <Card className="card-elevated h-full overflow-hidden">
            <CardHeader className="px-6 py-5 bg-muted/30 border-b">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl font-bold text-foreground">
                    SMS Overview
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Live SMS activity counters across your campaigns
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {smsStatCards.map((stat) => (
                  <StatBox
                    key={stat.name}
                    title={stat.name}
                    value={stat.value}
                    icon={stat.icon}
                    iconBgColor={stat.bgColor}
                    iconColor={stat.iconColor}
                    variant={stat.variant}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Campaign Metrics Report */}
      <div className="grid grid-cols-1">
        <Card className="card-elevated h-full overflow-hidden">
            <CardHeader className="px-6 py-5 bg-muted/30 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <UserGroupIcon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-xl font-bold text-foreground">
                      Campaign lead statuses
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Recently used campaigns first. Showing {visibleCampaignRows.length} of {campaignRows.length}.
                    </p>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {campaignRows.length === 0 ? (
                <div className="px-6 py-8 text-sm text-muted-foreground">
                  No campaigns found.
                </div>
              ) : (
                <div className="max-h-[22rem] overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-muted text-muted-foreground">
                      <tr className="text-left">
                        <th className="px-6 py-3 font-medium">Campaign</th>
                        <th className="px-4 py-3 font-medium text-right">Total</th>
                        <th className="px-4 py-3 font-medium text-right">New</th>
                        <th className="px-4 py-3 font-medium text-right">Called</th>
                        <th className="px-4 py-3 font-medium text-right">Interested</th>
                        <th className="px-4 py-3 font-medium text-right">Not Interested</th>
                        <th className="px-4 py-3 font-medium text-right">Follow Up</th>
                        <th className="px-4 py-3 font-medium text-right">No Answer</th>
                        <th className="px-4 py-3 font-medium text-right">DNC</th>
                        <th className="px-4 py-3 font-medium text-right">Failed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {visibleCampaignRows.map((campaign) => (
                        <tr key={campaign.campaignId} className="hover:bg-muted/20">
                          <td className="px-6 py-3">
                            <Link
                              to={`/campaigns/${campaign.campaignId}`}
                              className="font-medium text-foreground hover:text-primary"
                            >
                              {campaign.campaignName}
                            </Link>
                            <div className="text-xs text-muted-foreground capitalize mt-0.5">
                              {campaign.campaignStatus || "unknown"}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums">{campaign.stats.totalLeads.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{campaign.stats.remainingLeads.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{campaign.stats.calledLeads.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{campaign.stats.interestedLeads.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{campaign.stats.notInterestedLeads.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{campaign.stats.followUpLeads.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{campaign.stats.noAnswerLeads.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{campaign.stats.doNotCallLeads.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{campaign.stats.failedLeads.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {hasMoreCampaignRows && (
                <div className="px-6 py-4 border-t border-border bg-background">
                  <Button
                    variant="outlined"
                    size="sm"
                    onClick={() => setCampaignRowsVisible((n) => n + CAMPAIGN_ROWS_PAGE)}
                  >
                    Load more ({campaignRows.length - campaignRowsVisible} remaining)
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
      </div>

      <Card className="card-elevated overflow-hidden">
        <CardHeader className="px-6 py-5 bg-muted/30 border-b">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-muted">
              <PhoneIcon className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <CardTitle className="text-xl font-bold text-foreground">
                Call activity
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Dial counts for {dateFilterLabel}. Same numbers live on Monitor and Results.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {callActivityCards.map((stat) => (
              <StatBox
                key={stat.name}
                title={stat.name}
                value={stat.value}
                icon={stat.icon}
                iconBgColor={stat.bgColor}
                iconColor={stat.iconColor}
                variant={stat.variant}
              />
            ))}
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default DashboardPage;
